import { useCallback, useState } from 'react';
import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { useApplyCoverDesignMutation, usePostFileMutation } from '../../../redux/api/fymAPI';
import { coverErrorCode, coverFileRefusal, isCoverDocRefusal, type CoverFileRefusal } from '../doc/coverEditorErrors';
import { exportCoverJpeg } from '../render/exportCoverJpeg';
import { coverExportFileName } from '../render/exportRules';

/**
 * The step of "Use as cover" that failed, or the refusal of the API: `demo` (the example artist),
 * a refused cover file (`M24_COVER_FILE_INVALID`: `notSquare`, `tooSmall`, `notImage`,
 * `notOfAccount`), `docInvalid` (the design could not be saved first).
 */
export type UseAsCoverError = 'draw' | 'upload' | 'apply' | 'demo' | 'docInvalid' | CoverFileRefusal;

interface UseAsCoverInput {
  accountId: number;
  catalogItemUuid: string;
  designUuid: string;
  doc: CoverDoc;
  watermark: boolean;
  artist: string;
  title: string;
  /** Saves the open design first, so the applied version is the one on screen. */
  flush: () => Promise<void>;
  onDone: () => void;
}

/** What the dialog says for a failed "Use as cover": a known refusal of the API, else the step. Pure. */
export function coverApplyErrorOf(caught: unknown, step: 'draw' | 'upload' | 'apply'): UseAsCoverError {
  if (coverErrorCode(caught) === 'M24_DEMO_READ_ONLY') {
    return 'demo';
  }
  if (isCoverDocRefusal(caught)) {
    return 'docInvalid';
  }
  return coverFileRefusal(caught) ?? step;
}

/**
 * "Use as cover" (spec §5, §6): the one action of the editor that changes the song. It saves the
 * design, draws the JPEG in the browser, uploads it as a file of the artist (`POST .../files`) and
 * calls `POST .../designs/:uuid/apply`. A Free or trial workspace gets the same file as its
 * download, with the watermark (owner decision D7). `error` names the step that failed.
 */
export function useUseAsCover(input: UseAsCoverInput) {
  const { accountId, catalogItemUuid, designUuid, doc, watermark, artist, title, flush, onDone } = input;
  const [postFile] = usePostFileMutation();
  const [applyDesign] = useApplyCoverDesignMutation();
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<UseAsCoverError | null>(null);

  const apply = useCallback(async (): Promise<void> => {
    setIsBusy(true);
    setError(null);
    let step: UseAsCoverError = 'draw';
    try {
      await flush();
      const { blob, side } = await exportCoverJpeg(doc, { watermark });
      step = 'upload';
      const formData = new FormData();
      formData.append('file', blob, coverExportFileName({ artist, title, side }));
      const file = await postFile({ accountId, formData }).unwrap();
      step = 'apply';
      await applyDesign({ accountId, catalogItemUuid, designUuid, fileId: file.id }).unwrap();
      onDone();
    } catch (caught) {
      console.error(`Cover editor: "Use as cover" failed at the step ${step}`, caught);
      setError(coverApplyErrorOf(caught, step));
    } finally {
      setIsBusy(false);
    }
  }, [accountId, applyDesign, artist, catalogItemUuid, designUuid, doc, flush, onDone, postFile, title, watermark]);

  const clearError = useCallback(() => setError(null), []);
  return { apply, isBusy, error, clearError };
}
